import React, { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react'

import {
  Color,
  Text,
  FontWeight,
  withCitrusTheme,
  DisplayPosition,
  Box,
  Display,
  JustifyContent,
  TextAlign,
  AlignItems,
  ZIndex,
  BorderStyle,
} from '@gousto-internal/citrus-react'

import { RecipeUUID } from '@library/menu-service'
import { IngredientSlotValue } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { CrossOut } from './CrossOut'
import { Tooltip } from './Tooltip'
import { AlternativeOptions } from '../../types'

const IngredientSelectedStates = {
  Selected: 'selected',
  Available: 'available',
  NotAvailable: 'not-available',
  SoldOut: 'sold-out',
} as const

type IngredientSelectedState =
  typeof IngredientSelectedStates[keyof typeof IngredientSelectedStates]

const PillText = ({ name }: { name: string }) => (
  <Text
    size={1}
    fontWeight={FontWeight.Bold}
    textAlign={TextAlign.Center}
    style={{ whiteSpace: 'nowrap', zIndex: ZIndex.Top }}
  >
    {name}
  </Text>
)

const SelectedPill = ({ children }: { children: ReactNode }) => {
  const Component = withCitrusTheme(Box, (theme) => ({
    width: '100%',
    height: '40px',
    color: theme.color(Color.ColdGrey_800),
    backgroundColor: theme.color(Color.Secondary_50),
    borderStyle: BorderStyle.Solid,
    borderColor: theme.color(Color.Secondary_400),
    borderRadius: '100px',
    borderWidth: '2px',
    display: Display.Flex,
    justifyContent: JustifyContent.Center,
    alignItems: AlignItems.Center,
    paddingRight: '20px',
    paddingLeft: '20px',
  }))

  return <Component>{children}</Component>
}

const SelectablePill = ({
  state,
  children,
  onClick,
  slotValueId,
}: {
  state: IngredientSelectedState
  children: ReactNode
  onClick: (e: SyntheticEvent, ingredientValueId: string) => void
  slotValueId: string
}) => {
  const isAvailable = state === IngredientSelectedStates.Available

  const Component = withCitrusTheme(Box, (theme) => ({
    cursor: 'pointer',
    width: '100%',
    height: '40px',
    color: theme.color(isAvailable ? Color.ColdGrey_800 : Color.ColdGrey_500),
    backgroundColor: theme.color(Color.NeutralGrey_50),
    borderStyle: BorderStyle.Solid,
    borderColor: theme.color(Color.NeutralGrey_200),
    display: Display.Flex,
    justifyContent: JustifyContent.Center,
    alignItems: AlignItems.Center,
    borderRadius: '100px',
    borderWidth: '1px',
    paddingRight: '20px',
    paddingLeft: '20px',
    position: DisplayPosition.Relative,
    '&:hover': {
      backgroundColor: theme.color(Color.NeutralGrey_100),
      color: theme.color(Color.ColdGrey_800),
    },
    '&:active, &:focus': {
      outline: '2px solid #656667',
      borderColor: theme.color(Color.NeutralGrey_600),
    },
  }))

  return (
    <Component tabIndex={0} role="button" onClick={(e: SyntheticEvent) => onClick(e, slotValueId)}>
      {children}
    </Component>
  )
}

const getNextRecipeOption = (
  options: AlternativeOptions['options'],
  recipeUuid: RecipeUUID,
  ingredientValueId: string,
  slotId: string,
) => {
  const currentRecipeOption = options?.find((option) => option.recipe.id === recipeUuid)

  const slotValuesIfIngredientSelected = {
    ...(currentRecipeOption?.ingredientSlotValues || {}),
    [slotId]: ingredientValueId,
  }

  const nextRecipeOption = options?.find((option) => {
    const slotValues = option.ingredientSlotValues || {}

    const areSlotValuesEqual =
      JSON.stringify(Object.entries(slotValues).sort()) ===
      JSON.stringify(Object.entries(slotValuesIfIngredientSelected).sort())

    return areSlotValuesEqual
  })

  const isOffered = options?.some((option) => option.recipe.id === nextRecipeOption?.recipe.id)

  return isOffered ? nextRecipeOption : null
}

const getIngredientPillState = (
  options: AlternativeOptions['options'],
  recipeUuid: RecipeUUID,
  value: IngredientSlotValue,
  slotId: string,
): IngredientSelectedState => {
  const currentRecipeOption = options?.find((option) => option.recipe.id === recipeUuid)
  const isIngredientSlotInCurrentRecipe =
    currentRecipeOption?.ingredientSlotValues?.[slotId] === value.id

  if (isIngredientSlotInCurrentRecipe) {
    return IngredientSelectedStates.Selected
  }

  const nextRecipeOption = getNextRecipeOption(options, recipeUuid, value.id, slotId)

  if (!nextRecipeOption) {
    return IngredientSelectedStates.NotAvailable
  }

  if (nextRecipeOption && !nextRecipeOption.isOutOfStock) {
    return IngredientSelectedStates.Available
  }

  return IngredientSelectedStates.SoldOut
}

type IngredientPillProps = {
  slotId: string
  recipeUuid: RecipeUUID
  options: AlternativeOptions['options']
  slotValue: IngredientSlotValue
  onChangeSelection: (id: RecipeUUID) => void
}

export const IngredientPill = ({
  options,
  recipeUuid,
  slotValue,
  slotId,
  onChangeSelection,
}: IngredientPillProps) => {
  const [showTooltip, setShowTooltip] = useState(false)

  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [tooltipLeft, setTooltipLeft] = useState<number | null>(null)

  const state = getIngredientPillState(options, recipeUuid, slotValue, slotId)
  const isSoldOut = state === IngredientSelectedStates.SoldOut
  const isNotAvailable = state === IngredientSelectedStates.NotAvailable
  const isSelected = state === IngredientSelectedStates.Selected

  const onChange = (e: SyntheticEvent, ingredientValueId: string) => {
    e.stopPropagation()
    const recipeOption = getNextRecipeOption(options, recipeUuid, ingredientValueId, slotId)

    if (isNotAvailable) {
      setShowTooltip(true)
      return
    }

    if (recipeOption) {
      onChangeSelection(recipeOption?.recipe.id)
    }
  }

  let tooltipText = ''
  if (isNotAvailable) {
    tooltipText = 'Sorry, this isn’t available with the other options you’ve selected'
  }

  // Close tooltip on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false)
      }
    }

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTooltip])

  // Compute tooltip horizontal position
  useEffect(() => {
    if (showTooltip && tooltipRef.current && wrapperRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const wrapperRect = wrapperRef.current.getBoundingClientRect()

      let left = wrapperRect.width / 2 - tooltipRect.width / 2
      const viewportPadding = 8
      const absoluteLeft = wrapperRect.left + left

      if (absoluteLeft < viewportPadding) {
        left += viewportPadding - absoluteLeft
      } else if (absoluteLeft + tooltipRect.width > window.innerWidth - viewportPadding) {
        left -= absoluteLeft + tooltipRect.width - (window.innerWidth - viewportPadding)
      }

      setTooltipLeft(left)
    }
  }, [showTooltip])

  return (
    <Box position={DisplayPosition.Relative} ref={wrapperRef}>
      {isSelected ? (
        <SelectedPill>
          <PillText name={slotValue.name} />
        </SelectedPill>
      ) : (
        <SelectablePill
          state={state}
          onClick={(e) => onChange(e, slotValue.id)}
          slotValueId={slotValue.id}
        >
          <Box
            width="100%"
            display={Display.Flex}
            justifyContent={JustifyContent.Center}
            alignItems={AlignItems.Center}
            position={DisplayPosition.Absolute}
            top="0px"
            right="0px"
            bottom="0px"
            left="0px"
          >
            <Box width="75%" height="99%">
              {(isSoldOut || isNotAvailable) && <CrossOut />}
            </Box>
          </Box>
          <PillText name={slotValue.name} />
        </SelectablePill>
      )}
      {showTooltip && (
        <Box
          ref={tooltipRef}
          position={DisplayPosition.Absolute}
          top="100%"
          left={tooltipLeft !== null ? `${tooltipLeft}px` : '50%'}
          zIndex={ZIndex.Top}
          minWidth="180px"
          maxWidth="300px"
          width="auto"
          style={{
            marginTop: '15px',
            overflowWrap: 'break-word',
            transform: tooltipLeft === null ? 'translateX(-50%)' : undefined,
          }}
        >
          <Tooltip text={tooltipText} onClose={() => setShowTooltip(false)} />
        </Box>
      )}
    </Box>
  )
}
